import { Bell, Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import useViewport from '@/hooks/use-viewport';

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// NOTE: change version every time content changes
const VERSION = '6';
const STORAGE_KEY = 'announcement-toast-dismissed';

function AnnouncementToastContent() {
  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center gap-2 text-sm uppercase tracking-wide">
        <Info size={12} className="inline-block" /> Announcement
      </div>
      <p className="text-vestgrey-500">
        <span className="font-semibold text-foreground">
          CL roll schedule updated:
        </span>{' '}
        the next CL roll is now{' '}
        <span className="font-semibold text-foreground">Jun 11</span> instead of
        Jun 12. Future rolls will avoid Fridays and days before holidays.
      </p>
    </div>
  );
}

export default function AnnouncementToast() {
  const [isRead, setIsRead] = useState(false);
  const hasHydrated = useRef(false);
  const { i18n } = useTranslation();
  const currentLanguage = useRef(i18n.language);
  const toastIdRef = useRef<number | string | null>(null);

  const viewport = useViewport();

  const createAnnouncementToast = useCallback(() => {
    if (viewport === 'sm') return;
    return toast.info(<AnnouncementToastContent />, {
      position: 'bottom-right',
      duration: Infinity,
      onDismiss: () => {
        localStorage.setItem(STORAGE_KEY, VERSION);
        toastIdRef.current = null;
      },
    });
  }, [viewport]);

  useEffect(() => {
    if (currentLanguage.current !== i18n.language && toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      currentLanguage.current = i18n.language;
      toastIdRef.current = createAnnouncementToast() ?? null;
      return;
    }

    if (currentLanguage.current !== i18n.language) {
      currentLanguage.current = i18n.language;
    }
    if (hasHydrated.current) return;

    if (localStorage.getItem(STORAGE_KEY) === VERSION) {
      setIsRead(true);
      return;
    }

    hasHydrated.current = true;
    toastIdRef.current = createAnnouncementToast() ?? null;

    return () => {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [i18n.language, createAnnouncementToast]);

  if (viewport !== 'sm') return;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          setIsRead(true);
          localStorage.setItem(STORAGE_KEY, VERSION);
        }
      }}
    >
      <PopoverTrigger className="relative">
        <Bell size={14} />
        {!isRead && (
          <div className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-red" />
        )}
      </PopoverTrigger>
      <PopoverContent className="shadow-none">
        <AnnouncementToastContent />
      </PopoverContent>
    </Popover>
  );
}
